/**
 * JWT storage + refresh for the ERP web client (mirrors mobile auth-tokens flow).
 *
 * `createAuthTokenStore` builds one store (storage keys, session-expired event, extra request headers): the club ERP
 * uses the default store below (`my22.*` keys, `X-Club` of the current club); the League space (src/league) builds
 * its own store with `mysquash.*` keys and no `X-Club` (the League works across clubs).
 */

import { clubRequestHeaders } from '../currentClub';

const EXPIRY_SKEW_SEC = 60;

function readJwtExpSec(token) {
  try {
    const parts = String(token).split('.');
    if (parts.length !== 3) return null;
    const json = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = json.length % 4 === 0 ? '' : '='.repeat(4 - (json.length % 4));
    const payload = JSON.parse(atob(json + pad));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function isAccessTokenFresh(token) {
  const exp = readJwtExpSec(token);
  if (exp == null) return true;
  return exp > Math.floor(Date.now() / 1000) + EXPIRY_SKEW_SEC;
}

/**
 * @param {{ accessKey: string, refreshKey: string, sessionExpiredEvent: string, requestHeaders?: () => object }} options
 *   `requestHeaders` : extra headers of the refresh / logout calls (e.g. `X-Club`).
 */
export function createAuthTokenStore({ accessKey, refreshKey, sessionExpiredEvent, requestHeaders = () => ({}) }) {
  let refreshInFlight = null;

  function getStoredAccessToken() {
    try {
      return localStorage.getItem(accessKey);
    } catch {
      return null;
    }
  }

  function getStoredRefreshToken() {
    try {
      return localStorage.getItem(refreshKey);
    } catch {
      return null;
    }
  }

  function setStoredAccessToken(token) {
    try {
      if (!token) localStorage.removeItem(accessKey);
      else localStorage.setItem(accessKey, token);
    } catch {
      /* ignore quota */
    }
  }

  function setStoredRefreshToken(token) {
    try {
      if (!token) localStorage.removeItem(refreshKey);
      else localStorage.setItem(refreshKey, token);
    } catch {
      /* ignore */
    }
  }

  function clearStoredAuthTokens() {
    try {
      localStorage.removeItem(accessKey);
      localStorage.removeItem(refreshKey);
    } catch {
      /* ignore */
    }
  }

  function persistTokenPair(body) {
    const access = body?.access_token?.trim?.() || body?.access_token || null;
    const refresh = body?.refresh_token?.trim?.() || body?.refresh_token || null;
    if (!access) return null;
    setStoredAccessToken(access);
    if (refresh) setStoredRefreshToken(refresh);
    return access;
  }

  /**
   * @param {string} apiRoot - API origin without trailing slash
   */
  async function refreshAccessToken(apiRoot) {
    const refresh = getStoredRefreshToken();
    if (!refresh) return null;

    const root = String(apiRoot || '').replace(/\/+$/, '');
    try {
      const res = await fetch(`${root}/Player/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Host-Environment': '.net', ...requestHeaders() },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) clearStoredAuthTokens();
        return null;
      }
      const body = await res.json();
      return persistTokenPair(body);
    } catch {
      return null;
    }
  }

  function refreshAccessTokenOnce(apiRoot) {
    if (!refreshInFlight) {
      refreshInFlight = refreshAccessToken(apiRoot).finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  }

  /** Access token ready to send (proactive refresh if near expiry). */
  async function ensureAccessToken(apiRoot) {
    const current = getStoredAccessToken();
    if (current && isAccessTokenFresh(current)) return current;
    if (!current && !getStoredRefreshToken()) return null;
    return refreshAccessTokenOnce(apiRoot);
  }

  async function logoutRemote(apiRoot) {
    const refresh = getStoredRefreshToken();
    clearStoredAuthTokens();
    if (!refresh) return;
    const root = String(apiRoot || '').replace(/\/+$/, '');
    try {
      await fetch(`${root}/Player/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Host-Environment': '.net', ...requestHeaders() },
        body: JSON.stringify({ refresh_token: refresh }),
      });
    } catch {
      /* best-effort */
    }
  }

  function emitSessionExpired() {
    try {
      window.dispatchEvent(new CustomEvent(sessionExpiredEvent));
    } catch {
      /* ignore */
    }
  }

  return {
    SESSION_EXPIRED_EVENT: sessionExpiredEvent,
    getStoredAccessToken,
    getStoredRefreshToken,
    setStoredAccessToken,
    setStoredRefreshToken,
    clearStoredAuthTokens,
    persistTokenPair,
    refreshAccessToken,
    refreshAccessTokenOnce,
    ensureAccessToken,
    logoutRemote,
    emitSessionExpired,
  };
}

// ── Store of the club ERP (unchanged keys and behaviour) ──

const erpTokens = createAuthTokenStore({
  accessKey: 'my22.auth.accessToken',
  refreshKey: 'my22.auth.refreshToken',
  /** Dispatched on window when refresh fails → AuthProvider clears staff session. */
  sessionExpiredEvent: 'my22:sessionExpired',
  requestHeaders: clubRequestHeaders,
});

/** Dispatched on window when refresh fails → AuthProvider clears staff session. */
export const SESSION_EXPIRED_EVENT = erpTokens.SESSION_EXPIRED_EVENT;

export const {
  getStoredAccessToken,
  getStoredRefreshToken,
  setStoredAccessToken,
  setStoredRefreshToken,
  clearStoredAuthTokens,
  persistTokenPair,
  refreshAccessToken,
  refreshAccessTokenOnce,
  ensureAccessToken,
  logoutRemote,
  emitSessionExpired,
} = erpTokens;
